<template>
  <div class="checkout-page">
    <div class="nav-icon">
      <a href="/">
        <img src="/assets/images/logo-alt.png"
             alt="LM Logo"
             class="main-navigation__logo">
      </a>
    </div>
    <error-boundary
      title="Something has gone wrong"
      message="If this keeps happening, please contact us."
      class="checkout-page__wrapper"
      propagate>
      <template
        #default
        class="checkout-page__content"
        @error="onCheckoutError">
        <checkout v-model="currentStepAlias"
                  class="checkout-page__checkout"
                  @beforeChangeStep="onRequestChangeStep"
                  @changeStep="onChangeStep"
                  @loading="loading = $event">
          <checkout-progress></checkout-progress>
          <div v-show="loading">
            <loading-spinner size="2x"></loading-spinner>
          </div>
          <div v-show="!loading">
            <div class="my-steps">
              <your-details-step :stateless="submitted"
                                 show-register-now-button
                                 show-screen-name
                                 login-button-class="cta cta--primary--alt">
                <template #login>
                  <slot name="login"></slot>
                </template>

                <template #why-register="{ toggleRegistration, showRegisterNowButton }">
                  <h2>Create an account</h2>

                  <p>
                    Your account will be used to give you access to Leadership Matters.
                  </p>

                  <div v-if="showRegisterNowButton"
                       class="register-button-wrapper">
                    <button class="cta cta--primary--alt"
                            @click="toggleRegistration()">
                      Register now
                    </button>
                  </div>
                </template>

                <template #marketing-label>
                  <slot name="marketing-label"></slot>
                </template>

                <template #register-btn>
                  Register
                </template>
              </your-details-step>

              <basket-step :search-type-id="searchTypeId"
                           :stateless="submitted">
                <template #basket="{events, ...slotProps}">
                  <styled-basket v-bind="slotProps"
                                 no-delete-order-lines
                                 enable-vouchers
                                 v-on="events">
                  </styled-basket>
                </template>
              </basket-step>

              <metadata-step :stateless="submitted"></metadata-step>

              <payment-step :terms-link="termsLink"
                            :stateless="submitted"
                            :card-payment-icons="paymentIcons"
                            :sealed.sync="submitted">
              </payment-step>

              <confirmation-step assign-subscriptions
                                 button-class="btn btn-primary"
                                 back-to-home-text="Access Site"
                                 @submit="onSubmit">
                <template #subscription-manager>
                  <subscriptions-manager heading="Assign Subscriptions"
                                         use-action-icons>
                    <template #loading>
                      <loading-spinner size="2x"></loading-spinner>
                    </template>
                  </subscriptions-manager>
                </template>
              </confirmation-step>
            </div>
          </div>
        </checkout>
      </template>
    </error-boundary>
  </div>
</template>

<script>
import Vue from "vue";
import VueScrollTo from "vue-scrollto";
import {
  YourDetailsStep,
  BasketStep,
  PaymentStep,
  ConfirmationStep,
  MetadataStep,
  BasketResource,
} from "spiritweb.packages.ecommerce";
import { SubscriptionsManager } from "spiritweb.packages.web-account";
import StyledBasket from "~/Components/Basket/StyledBasket.vue";
import LoadingSpinner from "~/Components/Common/LoadingSpinner.vue";

Vue.use( VueScrollTo, {
  container: "#app",
  duration: 500,
  easing: "ease",
  offset: 0,
  force: true,
  cancelable: true,
  onStart: false,
  onDone: false,
  onCancel: false,
  x: false,
  y: true,
} );

export default {
  name: "CheckoutPage",
  components: {
    YourDetailsStep,
    BasketStep,
    PaymentStep,
    ConfirmationStep,
    MetadataStep,
    StyledBasket,
    LoadingSpinner,
    SubscriptionsManager,
  },
  props: {
    termsLink: {
      type: String,
      default: null,
    },
    searchTypeId: {
      type: String,
      default: null,
    },
  },
  data: () => ( {
    currentStepAlias: null,
    loading: true,
    waiting: true,
    hash: "",
    submitted: false,
    paymentIcons: [
      {
        src: "/Assets/images/default-payment-icons/visa.png",
        alt: "VISA",
      },
    ],
  } ),
  computed: {
    isLoading () {
      return this.loading || this.waiting;
    },
  },
  watch: {
    currentStepAlias: {
      handler ( alias ) {
        window.location.hash = alias || "";
      },
    },
    hash ( val ) {
      if ( val !== this.currentStepAlias ) {
        this.currentStepAlias = val;
      }
    },
  },
  beforeCreate () {
    const promise = BasketResource.getItemCount()
      .then( ( { data } ) => {
        if ( !data ) {
          this.submitted = true;
        }
      } );

    return promise;
  },
  created () {
    window.addEventListener( "hashchange", this.onHashChange );
    const hash = this.onHashChange();
    if ( hash ) {
      this.hash = hash;
    }

    this.waiting = false;
  },
  beforeDestroy () {
    window.removeEventListener( "hashchange", this.onHashChange );
  },
  methods: {
    onSubmit ( val ) {
      this.submitted = val;
    },
    onHashChange () {
      const { hash } = window.location;
      let result = null;
      if ( hash ) {
        result = hash.replace( "#", "" );
      }
      this.hash = result;
    },
    onRequestChangeStep ( alias, resolve, reject ) {
      try {
        this.$scrollTo( ".checkout-page", 300, {
          offset: -50,
          onDone: () => resolve(),
        } );
      } catch ( e ) {
        reject( e );
      }
    },
    onChangeStep ( step ) {
      this.currentStepAlias = step;
    },
    onCheckoutError () {
      window.location.hash = "error";
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.checkout-page {
  margin: 15px auto;

  .main-navigation__logo {
    margin-left: 0;
    margin-bottom: 15px;
  }

  &__content {
    margin-top: 10px;
    background: @bg-color;
    padding: 30px;
  }
}

/deep/ .checkout-progress {
  margin-bottom: 30px;

  &__list {
    position: relative;
    margin: 0;
    padding: 10px 30px;
    background: @bg-color;
    justify-content: space-between;
    display: flex;
    border-radius: 5px;
    box-shadow: 0 0 5px 0 rgba( .2, .2, .2, .2 );

    &::before {
      content: "";
      position: absolute;
      border-bottom: 2px solid @color-accent-1;
      left: 20px;
      right: 20px;
      top: 50%;
      z-index: 1;
    }
  }

  &__item {
    display: flex;
    color: @color-accent-1;

    &--disabled {
      color: #797979;

      .checkout-progress__btn {
        cursor: not-allowed;
      }
    }

    &--current {
      color: @color-accent-1;
    }
  }

  &__btn {
    position: relative;
    z-index: 2;
    background: @bg-color;
    border: 0;
    color: inherit;
    font-weight: 600;
    font-size: 16px;
    padding: 0 15px;
    margin: 0;
    -webkit-appearance: none;
  }

  @media ( max-width: 767px ) {
    visibility: hidden;
    display: none;
  }
}

/deep/ .tabbed-step {
  margin-bottom: 0 !important;
  background: #fff;
  padding: 20px 30px 30px;
  border-radius: 5px;
  box-shadow: 0 0 5px 0 rgba( .2, .2, .2, .2 );

  &.tabbed-step--invalid {
    .step__continue:not( .no-disabled ) {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .styled-basket {
    padding: 0;
    box-shadow: none;
  }
}

/deep/ .step__heading {
  margin-bottom: 30px;
}

/deep/ .step__footer {
  margin-top: 20px;
}

/deep/ .confirmation-step__back {
  margin-top: 0 !important;
}

/deep/ .confirmation-step__assign-button,
/deep/ .step__footer-button,
/deep/ .confirmation-step__back {
  margin-top: 30px;
  margin-right: 5px;
  background: @color-accent-1;
  border: 0;
  border-radius: 5px;
  color: #fff;
  padding: 10px 30px;
  text-decoration: none;
  font-weight: 400;

  &:last-child {
    margin-right: 0;
  }

  &:hover {
    background: @color-accent-1-dark;
  }
}
</style>
